import { Drawer } from '@/components/drawer';
import { Input, NumberInput, PhoneInput, SelectInput, SwitchInput, TextAreaInput } from '@/components/form';
import { useCurrencyList } from '@/features/currency-exchange/hooks';
import { useUserList } from '@/features/users/hooks';
import { Team } from '@/features/users/constants';
import { DrawerSizeEnum, PlacementEnum } from '@/types';
import { REGION_FILTERS_OPTIONS } from '@/utils/constant/appConstant';
import { Alert, Col, Form, Radio, Row, Select, Typography } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { IoPersonAddOutline } from 'react-icons/io5';
import { MdOutlineEdit } from 'react-icons/md';
import { CREDIT_TERMS_OPTIONS, CUSTOMER_TYPE_OPTIONS } from '../constants';

const { Title } = Typography;

interface VSSetupFormModalProps {
  open: boolean;
  isEditMode: boolean;
  onCancel: () => void;
  isLoading: boolean;
  isRegionAll?: boolean;
  userRegion?: number;
}

export const VSSetupFormModal: React.FC<VSSetupFormModalProps> = ({
  open,
  isEditMode,
  onCancel,
  isLoading,
  isRegionAll = true,
  userRegion,
}) => {
  const form = Form.useFormInstance();
  const selectedRegion = Form.useWatch('Region');
  const selectedRSM = Form.useWatch('Assigned RSM');

  // Filter region options based on user access
  const regionOptions = useMemo(() => {
    if (isRegionAll) {
      return REGION_FILTERS_OPTIONS;
    }
    return REGION_FILTERS_OPTIONS.filter(option => option.value === userRegion);
  }, [isRegionAll, userRegion]);

  // Build filter for RSM users: active + RSM team + region if selected
  const rsmFilter = useMemo(() => {
    const conditions = [
      `dogma_userstatus eq 1`,
      `dogma_team eq ${Team.REGIONAL_SALES_TEAM}`,
      ...(selectedRegion ? [`dogma_region eq ${selectedRegion}`] : []),
    ];
    return `&$filter=${conditions.join(' and ')}`;
  }, [selectedRegion]);

  const { data: rsmUsersData, isLoading: isRsmLoading } = useUserList({
    queryParams: { current: 1, pageSize: 200, filter: rsmFilter },
    enabled: open,
  });

  const rsmOptions = useMemo(
    () =>
      (rsmUsersData?.value ?? []).map((u) => {
        const name = [u.firstname, u.lastname].filter(Boolean).join(' ') || u.internalemailaddress || u.domainname || u.systemuserid;
        const regionLabel = REGION_FILTERS_OPTIONS.find((r) => r.value === u.dogma_region)?.label;
        return {
          value: u.systemuserid,
          label: regionLabel ? `${name} (${regionLabel})` : name,
        };
      }),
    [rsmUsersData]
  );

  // Reset RSM if selected RSM is not in the filtered list after region change
  useEffect(() => {
    if (!selectedRegion || !selectedRSM) return;
    const isValid = rsmOptions.some((opt) => opt.value === selectedRSM);
    if (!isValid) {
      form.setFieldValue('Assigned RSM', undefined);
    }
  }, [rsmOptions, selectedRegion]);

  const [currencySearch, setCurrencySearch] = useState('');
  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch currencies with server-side search filter
  const { data: currencyListData, isLoading: isCurrencyLoading } = useCurrencyList({
    queryParams: {
      current: 1,
      pageSize: 50,
      filter: currencySearch
        ? `&$filter=contains(dogma_name,'${currencySearch}') or contains(dogma_isocurrencycode,'${currencySearch}')`
        : '',
    },
    enabled: open,
  });

  const handleCurrencySearch = (value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => setCurrencySearch(value), 400);
  };

  const currencyOptions = useMemo(
    () =>
      (currencyListData?.value ?? []).map((c) => ({
        value: c.dogma_currencyid,
        label: `${c.dogma_name} (${c.dogma_isocurrencycode})`,
      })),
    [currencyListData]
  );

  return (
    <Drawer
      title={isEditMode ? 'Edit Customer Onboarding' : 'Victorian Sliders Setup'}
      subTitle="Configure the account that Victorian Sliders will manage"
      open={open}
      onClose={onCancel}
      loading={isLoading}
      placement={PlacementEnum.bottom}
      size={DrawerSizeEnum.large}
      saveBtnText={isEditMode ? 'Update Details' : 'Create Account'}
      saveBtnIcon={isEditMode ? <MdOutlineEdit /> : <IoPersonAddOutline />}
      // onSave={onSubmit}
    >
      <div style={{ marginTop: 24 }}>
        {/* Customer Type */}
        <Form.Item
          name="Customer Type"
          label="Customer Type"
          rules={[{ required: true, message: 'Please select customer type' }]}
        >
          <Radio.Group buttonStyle="solid">
            {CUSTOMER_TYPE_OPTIONS.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        {/* Company Information */}
        <Title level={5} style={{ marginTop: 16, marginBottom: 16 }}>
          Company Information
        </Title>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Input
              name="Company Name"
              label="Company Name"
              placeholder="Enter company name"
              required
              normalize={(value: string) => value?.trimStart()}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              name="Trading Name"
              label="Trading Name (if different)"
              placeholder="Enter trading name"
              normalize={(value: string) => value?.trimStart()}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Input
              name="Prefix"
              label="Sage Number"
              placeholder="Enter Sage Number"
              required
              normalize={(value: string) => value?.trimStart()}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Input
              name="Primary Contact Name"
              label="Primary Contact Name"
              placeholder="Enter contact name"
              required
              normalize={(value: string) => value?.trimStart()}
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              name="Primary Contact Email"
              label="Primary Contact Email (Admin Login)"
              placeholder="Enter email address"
              help="This will be their login username"
              required
              // readOnly={isEditMode}
              rules={[
                { type: 'email', message: 'Please enter a valid email address' },
                { pattern: /^[^+]*$/, message: 'Email addresses cannot contain the + character' },
              ]}
              normalize={(value: string) => value?.replace(/\s/g, '')}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <PhoneInput
              name="Primary Contact Phone"
              label="Primary Contact Phone"
              placeholder="Enter phone number"
              required
              maxLength={15}
            />
          </Col>
          <Col xs={24} md={12}>
            <SelectInput
              name="Region"
              label="Region"
              options={regionOptions}
              placeholder="Select region"
              required
            />
          </Col>
        </Row>

        <TextAreaInput
          name="Company Address"
          label="Company Address"
          minRows={2}
          placeholder="Enter company address"
          required
        />

        {/* Account Configuration */}
        <Title level={5} style={{ marginTop: 24, marginBottom: 16 }}>
          Account Configuration (Victorian Sliders Sets)
        </Title>

        <Alert
          title="Customer will set their own margins, branding, and T&Cs in their portal."
          type="info"
          style={{ marginBottom: 24 }}
        />

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <NumberInput
              name="Quote Validity (days)"
              label="Quote Validity (days)"
              placeholder="Enter validity days"
              min={1}
              required
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="Credit Terms"
              label="Credit Terms"
              rules={[{ required: true, message: 'Please select credit terms' }]}
            >
              <Radio.Group buttonStyle="solid">
                {CREDIT_TERMS_OPTIONS.map((option) => (
                  <Radio.Button key={option.value} value={option.value}>
                    {option.label}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <SelectInput
              name="Assigned RSM"
              label="Assign to RSM"
              options={rsmOptions}
              placeholder={isRsmLoading ? 'Loading...' : 'Select RSM'}
              hideClear={false}
              loading={isRsmLoading}
            />
          </Col>
          <Col xs={24} md={12}>
            <div className="custom_form_comp_wrapper">
              <label className="block text-primary-0 mb-2">Currency</label>
              <Form.Item name="Currency" rules={[{ required: true, message: 'Please select a currency' }]}>
                <Select
                  showSearch
                  allowClear
                  placeholder="Search by name or ISO code"
                  loading={isCurrencyLoading}
                  options={currencyOptions}
                  filterOption={false}
                  onSearch={handleCurrencySearch}
                  size="large"
                  className="bg-neutral-150 rounded text-sm border-[#F8FAFC]"
                />
              </Form.Item>
            </div>
          </Col>
        </Row>
      </div>
    </Drawer>
  );
};
